import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import Panzoom, { PanZoom } from 'panzoom';
import { cloneDeep, filter, uniq } from 'lodash-es';
import mermaid from 'mermaid';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { nodeType, DMConstants } from 'src/app/pages/designs/data-model/data-model.component';
import { AppLoaderService } from 'src/app/services/app-loader.service';

@Component({
  selector: 'vb-data-model-visualizer',
  templateUrl: './data-model-visualizer.component.html',
  styleUrls: ['./data-model-visualizer.component.scss'],
  standalone: false  //NOSONAR
})
export class DataModelVisualizerComponent implements OnInit, OnDestroy {
  private appGlobalService = inject(AppGlobalService);
  private loaderService = inject(AppLoaderService);

  appId = '';

  zoomElement: PanZoom;

  @ViewChild('mermaid')
  public mermaidDiv;

  classDgm = '';

  visualizerGroups: any = [];

  existingVisNameSpace = '';

  // Input elements
  @Input()
  dataModelDataOrder: any;

  @Input()
  referredTableData: any;

  @Input()
  tablesInfo: any;

  @Input()
  tableMap: any;

  @Input()
  refAppMetaInfos: any[];

  @Input()
  fkDeats: any;

  @Input()
  tableFieldMap: any;

  @Input()
  complexTables: any[];

  @Input()
  fullScreen = true;

  mermaidReservedKeywords = {
    note: 'Note',
    click: 'Click',
    link: 'Link'
  };

  /**
   * Lifecycle hook that is called after the component is initialized.
   */
  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    setTimeout(() => {
      if (this.dataModelDataOrder?.length) {
        this.loaderService.show();
      }
      this.initClassDiagram();
    }, 100);
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   */
  ngOnDestroy(): void {
    $(this.mermaidDiv?.nativeElement).remove();
    this.zoomElement?.dispose();
  }

  /**
   * Initializes the class diagram.
   */
  initClassDiagram(): void {
    this.zoomElement = Panzoom(this.mermaidDiv.nativeElement);
    this.initiateMermaid(this.referredTableData).catch((error) => console.error(error));
  }

  /**
   * This method iterates over the fields of a table and adds them to the class diagram.
   * @param col The column object.
   * @param classDiagram The class diagram string.
   * @param wiringInfo The wiring information object.
   * @param tableId The ID of the table.
   * @param tblInfo The table information object.
   * @returns The updated class diagram string.
   */
  async initiateMermaid(lookupData?: any) {
    //Grouping the classes
    const groupedCounts = {
      available: {},
      processed: {}
    };
    let data: any;
    let classDiagram = 'classDiagram\n';
    
    // Reset namespace tracking for fresh diagram generation
    this.existingVisNameSpace = '';
    this.visualizerGroups = [];
    
    if (this.dataModelDataOrder) {
      data = cloneDeep(this.dataModelDataOrder);
    }

    if (!data) {
      return;
    }
    if (!Array.isArray(data)) {
      data = data.children;
    }
    if (lookupData.length > 0) {
      lookupData.map((obj) => data.push(obj));
      const t = data.sort(function (a, b) {
        const titleA = a.appName?.toUpperCase() || '';
        const titleB = b.appName?.toUpperCase() || '';
        if (titleA < titleB) {
          return -1;
        }
        if (titleA > titleB) {
          return 1;
        }
        return 0;
      });
      data.forEach((obj) => {
        const value = obj.referredapp || obj.data?.aid;
        if (value) {
          groupedCounts.available[value] = (groupedCounts.available[value] || 0) + 1;
          groupedCounts.processed[value] = 0;
        }
      });
    }
    if (data.length === 0) {
      return;
    }
    const mermaidData = data;
    const wiringInfo = {};
    if (this.tablesInfo.length === 0) {
      return;
    }
    mermaidData.forEach((e: any) => {
      classDiagram = this.iterateOverGroup(e, classDiagram, wiringInfo, groupedCounts);
    });
    for (const key in wiringInfo) {
      if (Object.prototype.hasOwnProperty.call(wiringInfo, key)) {
        const tableName = this.tableMap[key] || this.getMetaName(key);
        let name = tableName.replaceAll(' ', '_');
        if (Object.keys(this.mermaidReservedKeywords).indexOf(name) !== -1) {
          name = this.mermaidReservedKeywords[name];
        }
        const tableInfo = wiringInfo[key];
        if (tableInfo?.lookup) {
          const lookup = tableInfo?.lookup;
          for (const tId in lookup) {
            if (Object.prototype.hasOwnProperty.call(lookup, tId)) {
              const tName = this.tableMap[tId] || this.getMetaName(tId);
              if (tName) {
                const fields = lookup[tId].join('<br/>');
                let pName = tName.replaceAll(' ', '_');
                if (Object.keys(this.mermaidReservedKeywords).indexOf(pName) !== -1) {
                  pName = this.mermaidReservedKeywords[pName];
                }
                classDiagram += ` ${pName} <--${name} : ${fields} \n`;
              }
            }
          }
        }
        if (tableInfo?.complex) {
          const complex = tableInfo?.complex;
          for (const tId in complex) {
            if (Object.prototype.hasOwnProperty.call(complex, tId)) {
              const tName = this.tableMap[tId];
              if (tName) {
                const fields = complex[tId].join('<br/>');
                const pName = tName.replaceAll(' ', '_');
                /*    classDiagram += `${pName} o-- ${name} : ${fields} \n` */ // Don't show complex types in diagram
              }
            }
          }
        }
        if (tableInfo?.foreignKey) {
          const fk = tableInfo?.foreignKey;
          for (const tId in fk) {
            if (Object.prototype.hasOwnProperty.call(fk, tId)) {
              let tName = this.tableMap[tId] || this.getMetaName(tId);
              if (Object.keys(this.mermaidReservedKeywords).indexOf(tName) !== -1) {
                tName = this.mermaidReservedKeywords[tName];
              }
              if (tName) {
                // let foreignKeys = 'fk-' + fk[tId].join(",");
                const foreignKeys = fk[tId].join(',');
                let pName = tName.replaceAll(' ', '_');
                if (Object.keys(this.mermaidReservedKeywords).indexOf(pName) !== -1) {
                  pName = this.mermaidReservedKeywords[pName];
                }
                classDiagram += `${pName} <--${name} : ${foreignKeys} \n`;
              }
            }
          }
        }
      }
    }
    this.classDgm = classDiagram;
    if (!this.mermaidDiv) {
      return;
    }

    mermaid.init(this.mermaidDiv.nativeElement).catch((error) => console.error(error));
    mermaid.initialize({
      theme: 'base',
      securityLevel: 'loose',
      maxTextSize: 200000
    });

    await this.drawDiagram(classDiagram);
    this.fitToScreen(true);
    setTimeout(() => {
      this.applyColorToVisualizationGroups();
      this.addTooltipToMermaid();
      this.loaderService.hide();
    }, 10);
  }

  /**
   * This method iterates over the tables and adds them to the class diagram.
   * @param group The group object.
   * @param classDiagram The class diagram string.
   * @param wiringInfo The wiring information object.
   * @param groupedCounts The grouped counts object.
   * @returns The updated class diagram string.
   */
  iterateOverGroup(group: any, classDiagram: string, wiringInfo, groupedCounts?) {
    const self = this;
    if (group.type === nodeType.TABLE) {
      classDiagram = self.iterateOverTable(group, classDiagram, wiringInfo, groupedCounts);
    } else if (group.type === nodeType.GROUP) {
      group.children?.forEach(function (table: any) {
        classDiagram = self.iterateOverGroup(table, classDiagram, wiringInfo, groupedCounts);
      });
    }
    return classDiagram;
  }

  /**
   * This method retrieves the name of a meta data object given its ID.
   * @param id The ID of the meta data object.
   * @returns The name of the meta data object.
   */
  getMetaName(id) {
    const mdata: any = (this.refAppMetaInfos && this.refAppMetaInfos[id] ) ? this.refAppMetaInfos[id] : null;
    return mdata || 'noname';
  }

  /**
   * This method draws the class diagram.
   * @param classDiagram The class diagram string.
   */
  async drawDiagram(classDiagram) {
    try {
      const { svg } = await mermaid.render('diagram', classDiagram);
      this.mermaidDiv.nativeElement.innerHTML = svg;
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * This method zooms in on the class diagram.
   */
  zoomIn() {
    console.log(this.zoomElement);
    this.zoomElement?.zoomTo(0, 0, 2);
  }

  /**
   * This method zooms out on the class diagram.
   */
  zoomOut() {
    this.zoomElement?.zoomTo(0, 0, 1);
    this.zoomElement?.smoothZoom(0, 0, 0.5);
  }

  /**
   * This method fits the class diagram to the screen.
   * @param fromInit A boolean value indicating whether the method is called from the init method.
   */
  fitToScreen(fromInit) {
    if (!fromInit) {
      if ($('.mermaid-wrapper').hasClass('mermaid-fullscreen')) {
        $('.mermaid-wrapper').removeClass('mermaid-fullscreen');
        $('.mermaid-wrapper').addClass('reset-mermaid-fullscreen');
        $('.mermaid-wrapper .icon-expand').removeClass('d-none');
        $('.mermaid-wrapper .icon-collapse').addClass('d-none');
      } else {
        $('.mermaid-wrapper').addClass('mermaid-fullscreen');
        $('.mermaid-wrapper').removeClass('reset-mermaid-fullscreen');
        $('.mermaid-wrapper .icon-expand').addClass('d-none');
        $('.mermaid-wrapper .icon-collapse').removeClass('d-none');
      }
    }
    this.zoomElement?.moveTo(0, 0);
    this.zoomElement?.zoomAbs(0, 0, 1);
  }

  /**
   * This method applies color to the visualization groups.
   */
  applyColorToVisualizationGroups() {
    this.visualizerGroups = uniq(this.visualizerGroups);
    //Referred apps
    this.visualizerGroups.forEach((id) => {
      const eleId = id.replaceAll(' ', '_');
      const element: any = document.getElementById(eleId)?.firstElementChild;
      if(element && element?.style){
      element.style.fill = 'rgba(168, 168, 168, 0.08)'; // Apply fill color
      element.style.stroke = 'rgba(168, 168, 168, 1)'; // Apply stroke color
      }
    });
    //current app Group
    const currentAppId = (this.appGlobalService.get('appmetaInfo')[this.appId] || '').replaceAll(' ', '_');
    const element: any = document.getElementById(currentAppId)?.firstElementChild;
    if (element) {
      element.style.fill = 'rgb(26 115 232 / 12%)'; // Apply fill color
      element.style.stroke = 'rgb(26 115 232)'; // Apply stroke color
    }
  }

  /**
   * This method adds tooltip to the mermaid diagram.
   */
  addTooltipToMermaid() {
    const _self = this;
    const elements = document.querySelectorAll('span.edgeLabel span.edgeLabel');
    elements.forEach(function (element) {
      element.addEventListener('mouseover', function (event) {
        const tooltipText = this.textContent.split(',');
        const tooltip = document.getElementById('tooltip');
        const fields: any = [];
        tooltipText.forEach((fkName) => {
          fields.push(_self.fkDeats[fkName]);
        });
        if (tooltip) {
          tooltip.textContent = fields.join(',');
          tooltip.style.display = 'block';

          const rect = element.getBoundingClientRect();

          // Calculate the position of the tooltip relative to the viewport
          const tooltipX = rect.left + window.pageXOffset + rect.width + 5;
          const tooltipY = rect.top + window.pageYOffset - tooltip.offsetHeight / 2 + rect.height / 2;

          // Set the position of the tooltip
          tooltip.style.left = `${tooltipX - 300}px`;
          tooltip.style.top = `${tooltipY - 100}px`;
        }
      });

      element.addEventListener('mouseout', function () {
        const tooltip = document.getElementById('tooltip');
        if (tooltip) {
          tooltip.style.display = 'none';
        }
      });
    });
  }

  /**
   * This method iterates over the tables and adds them to the class diagram.
   * @param table The table object.
   * @param classDiagram The class diagram string.
   * @param wiringInfo The wiring information object.
   * @param groupedCounts The grouped counts object.
   * @returns The updated class diagram string.
   */
  iterateOverTable(table: any, classDiagram: string, wiringInfo, groupedCounts) {
    const _self = this;
    const tableId = table?.data?.sid;
    const nameSpaceId = table.referredapp || table.data.aid;
    const spacename = table.referredapp
      ? this.getMetaName(table.referredapp)
      : this.appGlobalService.get('appmetaInfo')[table.data.aid];
    const nameSpace = (spacename || '').replaceAll(' ', '_');
    //let hasChildern = this.hasonlyFieldGroups(table);
    if (table.type === nodeType.TABLE) {
      wiringInfo[tableId] = {};
      let mermaidTId = table.title.replaceAll(' ', '_');
      if (Object.keys(this.mermaidReservedKeywords).indexOf(mermaidTId) !== -1) {
        mermaidTId = this.mermaidReservedKeywords[mermaidTId];
      }
      if (table.data?.t_pTable) {
        let tableName = table?.data?.t_pTable || table?.t_pTable;
        const tId = table?.data?.t_pTableId || table?.t_pTableId;
        tableName = this.tableMap[tId];
        if (tableName) {
          const mermaidPTId = tableName.replaceAll(' ', '_');
          // if(this.isRdbms) {
          //   classDiagram = classDiagram + `${mermaidPTId} <|-- ${mermaidTId}  : ParentTableRef \n`;
          // } else {
          classDiagram = `${classDiagram}${mermaidPTId} <|-- ${mermaidTId}  \n`;
          // }
        }
      }
      if (!table.referredapp) {
        if (table?.data?.foreignKey || table?.data?.t_fKey) {
          const obj = {};
          const fks = table.data.foreignKey ? table.data.foreignKey : JSON.parse(table?.data?.t_fKey);
          if (Array.isArray(fks)) {
            fks.forEach((fk) => {
              if (!fk.parentChildTableFk) {
                if (fk.mappings) {
                  const tempFields: any = [];
                  fk.mappings.forEach((fld) => {
                    tempFields.push(this.tableFieldMap[fld.child]);
                  });
                  this.fkDeats[fk.fkName] = tempFields.join();
                }
                if (obj[fk.parentTableSid]) {
                  obj[fk.parentTableSid].push(fk.fkName);
                } else {
                  obj[fk.parentTableSid] = [fk.fkName];
                }
              }
            });
          }
          if (Object.keys(obj).length) {
            wiringInfo[tableId].foreignKey = obj;
          }
        }
      }
      if (Object.keys(groupedCounts.available).length > 0) {
        groupedCounts.processed[nameSpaceId] = (groupedCounts.processed[nameSpaceId] || 0) + 1;
        const isExistingNamespace = this.existingVisNameSpace === nameSpaceId;
        
        // Close previous namespace if we're switching to a new one
        if (!isExistingNamespace && this.existingVisNameSpace && this.existingVisNameSpace !== '') {
          classDiagram += '}\n'; // Close the previous namespace
        }
        
        classDiagram += `${isExistingNamespace ? 'class' : `namespace ${nameSpace} { \nclass`} ${mermaidTId} ${table.children?.length > 0 ? '{\n' : '\n'}`;
        if (!isExistingNamespace) {
          this.visualizerGroups.push(nameSpace);
        }
        this.existingVisNameSpace = nameSpaceId;
      } else {
        classDiagram = `${classDiagram}class ${mermaidTId} ${table.children?.length > 0 ? '{\n' : '\n'}`;
      }
    }
    table.children?.forEach(function (col: any) {
      classDiagram = _self.iterateOverFields(col, classDiagram, wiringInfo, tableId, table);
    });

    if (
      Object.keys(groupedCounts.available).length > 0 &&
      nameSpaceId &&
      groupedCounts.processed[nameSpaceId] === groupedCounts.available[nameSpaceId]
    ) {
      // Close both the class (if it has children) and the namespace
      classDiagram += `${table.children?.length > 0 ? '}\n}\n' : '}\n'}`;
      // Reset the existing namespace since this one is now complete
      this.existingVisNameSpace = '';
    } else {
      classDiagram += `${table.children?.length > 0 ? '}\n' : ''}`;
    }

    return classDiagram;
  }

  /**
   * This method iterates over the fields of a table and adds them to the class diagram.
   * @param col The column object.
   * @param classDiagram The class diagram string.
   * @param wiringInfo The wiring information object.
   * @param tableId The ID of the table.
   * @param tblInfo The table information object.
   * @returns The updated class diagram string.
   */
  iterateOverFields(col: any, classDiagram, wiringInfo, tableId, tblInfo) {
    const _self = this;
    const REQ_LEN = 20;
    // Check if it's a FIELD type, or if it has no type but has field properties (for referred app fields)
    const isField = col.type === nodeType.FIELD || (!col.type && (col.f_type || col.name));
    const isFieldGroup = col.type === nodeType.FIELDGROUP;
    
    if (isField) {
      const fieldName = col.title || col.name || '';
      // fieldName = fieldName.length < REQ_LEN ? fieldName + '#0020;'.repeat(REQ_LEN - fieldName.length) : fieldName;
      let fieldType = col?.data?.f_type || col?.f_type;
      if (fieldType === DMConstants.COMPLEX_FIELD_TYPE) {
        const complexSid = col?.data?.f_cDataTypeId;
        const tblName = filter(this.complexTables, (o: any) => o.sid === complexSid);
        const isComplex = tblName.length > 0;
        if (isComplex) {
          fieldType = tblName[0]?.name;
          const complex = wiringInfo[tableId]?.complex;
          const id = complexSid;
          if (!complex) {
            wiringInfo[tableId].complex = { [id]: [fieldName] };
          } else if (complex[id]) {
            wiringInfo[tableId]?.complex[id].push(fieldName);
          } else {
            wiringInfo[tableId].complex[id] = [fieldName];
          }
        }
        // process complex types
      }
      if (fieldType === DMConstants.LOOKUP_FIELD_TYPE) {
        const lookupInfo = wiringInfo[tableId]?.lookup;
        const id = col?.data?.f_lookupTo || col?.f_lookupTo;
        if (id) {
          if (!lookupInfo) {
            wiringInfo[tableId].lookup = { [id]: [fieldName] };
          } else if (lookupInfo[id]) {
            wiringInfo[tableId]?.lookup[id].push(fieldName);
          } else {
            wiringInfo[tableId].lookup[id] = [fieldName];
          }
        }
      }
      classDiagram = `${classDiagram}${fieldName} : ${fieldType} \n`;
    } else if (isFieldGroup) {
      if (col.children && col.children.length > 0) {
        col.children?.forEach((e) => {
          classDiagram = _self.iterateOverFields(e, classDiagram, wiringInfo, tableId, tblInfo);
        });
      }
    }
    return classDiagram;
  }
  
}
